import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, CheckCircle2, Pencil, Trash2, Loader2, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stopwatch } from "@/components/Stopwatch";
import { JobTimer } from "@/components/JobTimer";
import { loadTimer, elapsedOf, clearTimer, setTimerMs, fmtHMS, parseHMS } from "@/lib/jobTimer";

export default function PendingJobs() {
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", client_id: "", category: "", amount: "", date: "", time: "" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [j, c] = await Promise.all([api.get("/jobs/pending"), api.get("/clients")]);
      setJobs(j.data);
      // Service clients (Cliente Servicios) go last; the rest alphabetical.
      const sorted = [...c.data].sort((a, b) =>
        (a.is_service ? 1 : 0) - (b.is_service ? 1 : 0) || a.name.localeCompare(b.name));
      setClients(sorted);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    if (clients.length === 0) { toast.error("Primero crea un cliente"); return; }
    setForm({ title: "", client_id: "", category: "", amount: "", date: "", time: "" }); setEditing(null); setOpen(true);
  };
  const openEdit = (j) => {
    setForm({ title: j.title, client_id: j.client_id, category: j.category || "",
      amount: j.amount != null ? String(j.amount) : "", date: j.date ? j.date.slice(0, 10) : "",
      time: fmtHMS(elapsedOf(loadTimer(j.id, j.time_ms || 0))) });
    setEditing(j.id); setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.client_id) { toast.error("Título y cliente son obligatorios"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, client_id: form.client_id, category: form.category,
        amount: parseFloat(form.amount) || 0, date: form.date || null,
      };
      if (editing) {
        payload.time_ms = parseHMS(form.time);
        await api.put(`/jobs/${editing}`, payload);
        setTimerMs(editing, payload.time_ms);
      } else {
        await api.post("/jobs", payload);
      }
      toast.success(editing ? "Trabajo actualizado" : "Trabajo creado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const markDone = async (job) => {
    setBusy(job.id);
    try {
      const ms = Math.round(elapsedOf(loadTimer(job.id, job.time_ms || 0)));
      await api.post(`/jobs/${job.id}/time`, { ms });
      await api.post(`/jobs/${job.id}/done`);
      clearTimer(job.id);
      toast.success("Trabajo realizado y añadido al historial del cliente");
      load();
    } catch (e) {
      toast.error("No se pudo actualizar");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (job) => {
    setBusy(job.id);
    try {
      await api.delete(`/jobs/${job.id}`);
      clearTimer(job.id);
      toast.success("Trabajo eliminado");
      load();
    } catch (e) {
      toast.error("No se pudo eliminar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="jobs-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Trabajos pendientes</h1>
          <p className="text-muted-foreground mt-1">{jobs.length} por hacer</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Stopwatch />
          <Button onClick={openNew} data-testid="new-job-button">
            <Plus className="h-4 w-4 sm:mr-1.5" /> <span className="hidden sm:inline">Nuevo trabajo</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : jobs.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-10 text-center text-muted-foreground">
          <ClipboardList className="h-8 w-8 mx-auto mb-3 opacity-40" />
          No tienes trabajos pendientes.
        </div>
      ) : (
        <div className="space-y-2" data-testid="jobs-list">
          {jobs.map((j) => (
            <div key={j.id} data-testid={`job-item-${j.id}`}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-4">
              <div className="min-w-0">
                <p className="font-medium truncate">{j.title}</p>
                <p className="text-sm text-muted-foreground truncate">{j.client_name}</p>
                <div className="mt-2">
                  <JobTimer job={j} />
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" onClick={() => markDone(j)} disabled={busy === j.id} data-testid={`job-done-${j.id}`}>
                  {busy === j.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  <span className="ml-1.5 hidden sm:inline">Realizado</span>
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(j)} data-testid={`job-edit-${j.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(j)} data-testid={`job-delete-${j.id}`}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="job-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar trabajo" : "Nuevo trabajo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Cliente *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="job-client-select"><SelectValue placeholder="Selecciona un cliente" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.is_service ? "🔧 " : ""}{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Título / descripción *</Label>
              <Textarea value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="job-title-input" placeholder="Qué hay que hacer" />
            </div>
            <div>
              <Label>Categoría (opcional)</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="job-category-input" placeholder="Soporte, Redes, CCTV…" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Dinero cobrado (€)</Label>
                <Input type="number" inputMode="decimal" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="job-amount-input" placeholder="0,00" />
              </div>
              <div>
                <Label>Fecha del trabajo</Label>
                <Input type="date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="job-date-input" />
              </div>
            </div>
            {editing && (
              <div>
                <Label>Tiempo dedicado (HH:MM:SS)</Label>
                <Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}
                  data-testid="job-time-input" placeholder="00:00:00" />
                <p className="text-xs text-muted-foreground mt-1">Puedes ajustar manualmente el tiempo del cronómetro de este trabajo.</p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              La fecha es la del día que hiciste el trabajo. Si la dejas vacía, al marcarlo como realizado se usará la fecha de hoy.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-job-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
